import { useMemo } from 'react';
import { FileCode, X } from 'lucide-react';

import { cn } from '@/lib/utils';
import { SlideDrawer } from '@/components/ui/slide-drawer';

import {
    CodeModuleEditor,
    JsonTreePanel,
    resolveRevisionFileContentView,
} from '@/components/document-view';

export interface FileContentDrawerPayload {
    filePath: string;
    content: string;
    contentType: string;
    sizeBytes: number;
}

interface FileContentDrawerProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    payload: FileContentDrawerPayload | null;
}

export function FileContentDrawer({ open, onOpenChange, payload }: FileContentDrawerProps) {
    const subtitle = payload
        ? `${payload.contentType} · ${payload.sizeBytes.toLocaleString()} bytes`
        : '';

    const contentView = useMemo(
        () =>
            payload
                ? resolveRevisionFileContentView({
                      filePath: payload.filePath,
                      contentType: payload.contentType,
                      content: payload.content,
                  })
                : null,
        [payload],
    );

    return (
        <SlideDrawer
            isOpen={open && !!payload}
            onClose={() => onOpenChange(false)}
        >
            {payload && (
                <>
                    <div
                        className="relative shrink-0 border-b border-slate-100 bg-slate-50/50 px-6 py-4 sm:px-8"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="file-content-drawer-title"
                    >
                        <div className="flex items-center justify-between gap-3">
                            <div className="flex min-w-0 items-center gap-3">
                                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-sky-100">
                                    <FileCode className="h-5 w-5 text-sky-600" aria-hidden />
                                </div>
                                <div className="min-w-0">
                                    <h2
                                        id="file-content-drawer-title"
                                        className="break-all font-mono text-xl font-semibold text-slate-900"
                                    >
                                        {payload.filePath}
                                    </h2>
                                    <p className="text-sm text-slate-500">{subtitle}</p>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onOpenChange(false);
                                }}
                                className="shrink-0 rounded-full p-1 text-slate-400 outline-none transition-colors hover:bg-slate-200 hover:text-slate-600 focus:ring-2 focus:ring-slate-900"
                            >
                                <X size={20} />
                                <span className="sr-only">Close</span>
                            </button>
                        </div>
                    </div>

                    <div className="flex min-h-0 min-w-0 flex-1 flex-col bg-white px-6 py-4 sm:px-8">
                        <div
                            className={cn(
                                'flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-lg border border-slate-200',
                                contentView?.mode === 'json-tree' ? 'bg-slate-950' : 'bg-black',
                            )}
                        >
                            {contentView?.mode === 'json-tree' ? (
                                <div className="min-h-0 flex-1 overflow-auto">
                                    <JsonTreePanel value={contentView.value} />
                                </div>
                            ) : contentView ? (
                                <CodeModuleEditor
                                    value={contentView.content}
                                    language={contentView.language}
                                    themePreset="fileDrawer"
                                    className="h-full min-h-0 flex-1"
                                />
                            ) : null}
                        </div>
                    </div>
                </>
            )}
        </SlideDrawer>
    );
}
